/**
 * The record of one run (specs/game-mechanics.md 2.8): the game fills it, the
 * leaderboard submits it and the end panel shows it. Pure logic: no PixiJS,
 * GSAP, DOM or JSON imports, so tests can import it directly.
 */

import { maxBonusHundredths, scoreFor } from './rules.js';

export const LEVEL_COUNT = 6;

/**
 * @param {string} name argument name, for the error message
 * @param {number} value
 */
function assertWholeNumber(name, value) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a whole number of 0 or more, got ${value}`);
  }
}

/**
 * Checks a level's rules (a levels.json row) and keeps what RunResult needs.
 * @param {number} levelNumber for the error message
 * @param {{waves: number, ducks: number, need: number, clock: number}} rules
 * @returns {{total: number, need: number, perWave: number, clock: number}}
 */
function levelRules(levelNumber, rules) {
  const { waves, ducks, need, clock } = rules || {};
  for (const [name, value] of Object.entries({ waves, ducks, need, clock })) {
    if (!Number.isInteger(value) || value < 1) {
      throw new RangeError(`startLevel(${levelNumber}): rules.${name} must be a whole number of 1 or more, got ${value}`);
    }
  }
  if (need > waves * ducks) {
    throw new RangeError(`startLevel(${levelNumber}): need ${need} is more than the ${waves * ducks} receipts`);
  }
  return { total: waves * ducks, need, perWave: ducks, clock };
}

/**
 * Builds a run the way the game plays it, and refuses anything submit_run
 * would reject (supabase/migrations/20261005000300_submit_run_v2.sql), so a
 * game bug throws in a test instead of a score going unrecorded. Per level:
 * a pass has caught at least `need` and at most one pounce past it (need +
 * per wave - 1), with at most maxBonusHundredths(clock) banked; a fail has
 * caught fewer than `need` and banks nothing; only the last level can fail.
 */
export class RunResult {
  constructor() {
    /** @type {number[]} receipts caught, one entry per level reached */
    this.receiptsByLevel = [];
    /** @type {number[]} banked time bonus in hundredths of a second, one entry per level reached (0 on a failed level) */
    this.bonusByLevel = [];
    /** @type {number} levels passed */
    this.levelsPassed = 0;
    /** @type {boolean} true once level 6 has passed and finishWin ran */
    this.won = false;
    /** @type {boolean} true once the run has ended, as a win or a loss */
    this.ended = false;
    // Whether the current (last started) level is still being played.
    this._open = false;
    // levelRules() for each level reached
    this._rules = [];
  }

  /** The level being played when the run ended (6 on a win); 0 before the first level. */
  get levelReached() {
    return this.receiptsByLevel.length;
  }

  /** Total receipts caught over every level reached. */
  get receipts() {
    return this.receiptsByLevel.reduce((a, b) => a + b, 0);
  }

  /** Total banked time bonus, in hundredths of a second, over every level reached. */
  get bonusHundredths() {
    return this.bonusByLevel.reduce((a, b) => a + b, 0);
  }

  /** 100 x receipts + each passed level's time bonus and level clear bonus (rules.js scoreFor). */
  get score() {
    return scoreFor(this.receiptsByLevel, this.bonusByLevel, this.levelsPassed);
  }

  /**
   * Begin the next level. Levels must come in order, 1 to 6, once each.
   * @param {number} levelNumber
   * @param {{waves: number, ducks: number, need: number, clock: number}} rules
   *   the level's row from levels.json
   * @returns {RunResult} this, for chaining
   */
  startLevel(levelNumber, rules) {
    this._assertNotEnded('startLevel');
    if (this._open) {
      throw new Error(`startLevel(${levelNumber}): level ${this.levelReached} is still in play`);
    }
    const expected = this.levelReached + 1;
    if (levelNumber !== expected) {
      throw new Error(`startLevel(${levelNumber}): expected level ${expected}`);
    }
    if (expected > LEVEL_COUNT) {
      throw new Error(`startLevel(${levelNumber}): a run has ${LEVEL_COUNT} levels`);
    }
    const checked = levelRules(levelNumber, rules);
    this.receiptsByLevel.push(0);
    this.bonusByLevel.push(0);
    this._rules.push(checked);
    this._open = true;
    return this;
  }

  /**
   * Add receipts caught to the current level.
   * @param {number} n whole number, 0 or more
   * @returns {RunResult}
   */
  addCatches(n) {
    this._assertOpen('addCatches');
    assertWholeNumber('addCatches', n);
    const rules = this._current();
    const caught = this.receiptsByLevel[this.levelReached - 1] + n;
    // the level clears at need, so the last pounce can take it at most a wave's worth less one past it
    const most = Math.min(rules.total, rules.need + rules.perWave - 1);
    if (caught > most) {
      throw new RangeError(`addCatches: ${caught} caught on level ${this.levelReached}, at most ${most} can be`);
    }
    this.receiptsByLevel[this.levelReached - 1] = caught;
    return this;
  }

  /**
   * Close the current level as a pass and bank its time bonus.
   * @param {number} bonusHundredths hundredths of a second left, 0 or more
   * @returns {RunResult}
   */
  passLevel(bonusHundredths) {
    this._assertOpen('passLevel');
    assertWholeNumber('passLevel', bonusHundredths);
    const rules = this._current();
    const caught = this.receiptsByLevel[this.levelReached - 1];
    if (caught < rules.need) {
      throw new Error(`passLevel: ${caught} caught on level ${this.levelReached}, below the need of ${rules.need}`);
    }
    if (bonusHundredths > maxBonusHundredths(rules.clock)) {
      throw new RangeError(`passLevel: ${bonusHundredths} hundredths banked on a ${rules.clock}s clock`);
    }
    this.bonusByLevel[this.levelReached - 1] = bonusHundredths;
    this.levelsPassed += 1;
    this._open = false;
    return this;
  }

  /**
   * End the run as a loss on the current level; its bonus stays 0.
   * @returns {RunResult}
   */
  failLevel() {
    this._assertOpen('failLevel');
    const rules = this._current();
    const caught = this.receiptsByLevel[this.levelReached - 1];
    if (caught >= rules.need) {
      throw new Error(`failLevel: ${caught} caught on level ${this.levelReached} meets the need of ${rules.need}`);
    }
    this._open = false;
    this.ended = true;
    return this;
  }

  /**
   * End the run as a win. Only valid once level 6 has passed.
   * @returns {RunResult}
   */
  finishWin() {
    this._assertNotEnded('finishWin');
    if (this._open || this.levelReached !== LEVEL_COUNT) {
      throw new Error(`finishWin: level ${LEVEL_COUNT} must be played and passed first`);
    }
    this.won = true;
    this.ended = true;
    return this;
  }

  /**
   * The arguments for the submit_run RPC.
   * @returns {{receipts_by_level: number[], bonus_by_level: number[]}}
   *   bonus_by_level is in hundredths of a second
   */
  toSubmitArgs() {
    return {
      receipts_by_level: this.receiptsByLevel.slice(),
      bonus_by_level: this.bonusByLevel.slice(),
    };
  }

  /** @returns {{total: number, need: number, perWave: number, clock: number}} the level in play's rules */
  _current() {
    return this._rules[this.levelReached - 1];
  }

  _assertNotEnded(method) {
    if (this.ended) throw new Error(`${method}: the run has already ended`);
  }

  _assertOpen(method) {
    this._assertNotEnded(method);
    if (!this._open) throw new Error(`${method}: no level is in play`);
  }
}

/**
 * Leaderboard label: a star for a full clear, "L<n>" (level reached) for a loss.
 * @param {{won: boolean, level_reached: number}} row
 * @returns {string}
 */
export function boardLabel({ won, level_reached }) {
  return won ? '★' : `L${level_reached}`;
}
